import Link from "next/link";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Image from "next/image";

import { useWindowSize } from "@/shared/hooks/useWindowSize";
import Screen from "@/constants/screens";
import { NowLinkItemsAuth } from "../utils";

import { Props } from "../types";
import routes from "@/constants/routes";
import { useScrollBlock } from "@/shared/hooks/useScrollBlock";
import { LogoBar } from "./LogoBar";
import { useAuth } from "@/shared/hooks/useAuth";
import { useRentalApplication } from "@/shared/hooks/useRentalApplication";

const HeaderMobile = ({ theme }: Props): JSX.Element => {
  const { width } = useWindowSize();
  const { isAuthenticated } = useAuth({ redirectOnUnauth: false });
  const { rentalApplication } = useRentalApplication({ redirectOnUnauth: false });
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [blockScroll, allowScroll] = useScrollBlock();

  const [activeDD, setIsActive] = useState("");

  const handleClick = (active: string | undefined) => {
    setIsActive(active === undefined || active === activeDD ? "" : active);
  };

  const toggleMenu = () => {
    setOpen((isMenuOpen) => !isMenuOpen);
  };

  useEffect(() => {
    if (width >= Screen.DESKTOP_1280) {
      setOpen(false);
      allowScroll();
    }

    if (width < Screen.DESKTOP_1280 && open) {
      blockScroll();
    }

    if (width < Screen.DESKTOP_1280 && !open) {
      allowScroll();
    }
  }, [width, open]);

  useEffect(() => {
    if (open) {
      allowScroll();
      setOpen(false);
    }
  }, [router]);

  useEffect(() => {
    // Ensure we never leave the app with scrolling blocked when this effect cleans up
    return () => allowScroll();
  });

  const renderAuthButton = () => {
    return rentalApplication?.status === "DRAFT" ? (
      <Link href={routes.rentalApplication.yourHome}>
        <a
          className={`flex w-11/12 text-center fixed xl:inline-block bottom-6 items-center justify-center font-bold px-4 py-2 text-base whitespace-nowrap transition duration-300 ease-in-out transform rounded shadow absolute-center-x xl:left-0 xl:translate-x-0 overflox-x-hidden xl:relative xl:bottom-auto xl:pb-3 h-11 xl:w-auto mr-2 ${
            theme === "ivory"
              ? "bg-landing-cherry text-white"
              : theme === "light"
              ? "bg-landing-orange text-landing-blue"
              : "bg-landing-orange text-landing-blue"
          } hover:scale-105 cursor-pointer
                 `}
        >
          Register
        </a>
      </Link>
    ) : (
      <Link href={routes.dashboard.root}>
        <a
          className={`flex w-11/12 text-center fixed xl:inline-block bottom-6 items-center justify-center font-bold px-4 py-2 text-base whitespace-nowrap transition duration-300 ease-in-out transform rounded shadow absolute-center-x xl:left-0 xl:translate-x-0 overflox-x-hidden xl:relative xl:bottom-auto xl:pb-3 h-11 xl:w-auto mr-2 ${
            theme === "ivory"
              ? "bg-landing-cherry text-white"
              : theme === "light"
              ? "bg-landing-orange text-landing-blue"
              : "bg-landing-orange text-landing-blue"
          } hover:scale-105 cursor-pointer
                 `}
        >
          Your Account
        </a>
      </Link>
    );
  };
  return (
    <div
      className={`sticky top-0 w-full z-[60] ${
        theme === "dark"
          ? "bg-landing-blue"
          : theme === "light"
          ? "bg-white"
          : theme === "lightBlue"
          ? "bg-[#F0F3FD]"
          : "bg-white"
      }`}
    >
      <nav
        className={`${open ? "fixed inset-0" : ""} ${
          theme === "dark"
            ? "bg-landing-blue"
            : theme === "light"
            ? "bg-white text-landing-blue"
            : theme === "lightBlue"
            ? "bg-[#F0F3FD] text-landing-blue"
            : "bg-landing-ivory"
        } h-full pb-3`}
      >
        {open ? (
          <div
            className={`w-full relative h-full overflow-scroll flex flex-col ${
              theme === "dark"
                ? "bg-landing-blue"
                : theme === "light"
                ? "bg-white"
                : theme === "lightBlue"
                ? "bg-[#F0F3FD] text-landing-blue"
                : "bg-landing-ivory"
            } overflow-hidden ${open ? "block" : ""}
            transition duration-300
            ${open ? "opacity-100 z-50 pointer-events-auto" : "opacity-0 -z-1 pointer-events-none"}
            `}
          >
            <LogoBar theme={theme} open={open} toggleMenu={toggleMenu} className="top-6" />

            <ul
              className={`flex relative xl:top-auto w-full flex-1 mt-10 overflow-y-auto px-5 md:px-20 flex-col text-center`}
            >
              {NowLinkItemsAuth.map((item, idx) => (
                <li
                  className={`mb-7 ${
                    idx + 1 === NowLinkItemsAuth.length ? "md:mb-20" : "md:mb-11"
                  }  ${idx === 0 ? "mt-16" : ""} ${""}`}
                  key={idx}
                >
                  {(!item.items && (
                    <Link href={item.href}>
                      <a className="relative flex flex-col mx-auto w-fit" href={item.href}>
                        <span
                          className={`inline-block relative text-sm font-semibold pb-2 ${
                            theme === "dark" ? "text-white" : "text-landing-blue"
                          }`}
                        >
                          {item.caption}
                        </span>
                        <span
                          className={`h-[1px] w-9/12 mt-1 absolute-center-x bottom-0  ${
                            router.pathname === item.href && "bg-landing-orange"
                          }`}
                        ></span>
                      </a>
                    </Link>
                  )) || (
                    <div
                      className={`group ${activeDD === item.caption ? "active" : ""} flex flex-col`}
                      onClick={() => handleClick(item.caption?.toString())}
                    >
                      <div className="relative flex flex-col mx-auto w-fit">
                        <span
                          className={`inline-block relative text-sm font-semibold pb-2 ${
                            theme === "dark" ? "text-white" : "text-landing-blue"
                          }`}
                        >
                          {item.caption}
                          <span className="ml-2">
                            <Image
                              src="/budget-calculator/chevron-orange-bottom.svg"
                              width={11}
                              height={6}
                              alt="down chevron"
                              className={`transition-all duration-300 ease-in-out group-[.active]:rotate-180 rotate-0`}
                            />
                          </span>
                        </span>
                        <span
                          className={`h-[1px] w-full hidden group-[.active]:flex mt-1 absolute-center-x bottom-0 bg-landing-orange`}
                        ></span>
                      </div>
                      <div
                        style={{
                          maxHeight: activeDD === item.caption ? "800px" : "0px",
                        }}
                        className="overflow-hidden flex flex-col transition-all duration-500 ease-in-out group-[.active]:mt-7"
                      >
                        {item.items?.map((item, idx) => (
                          <Link href={item.href} key={item.href}>
                            <a
                              className="relative flex flex-col mx-auto w-fit last:mb-0 mb-7 md:mb-11"
                              href={item.href}
                            >
                              <span
                                className={`inline-block relative text-sm font-semibold pb-2 ${
                                  theme === "dark" ? "text-white" : "text-landing-blue"
                                }`}
                              >
                                {item.caption}
                              </span>
                              <span
                                className={`h-[1px] w-9/12 mt-1 absolute-center-x bottom-0  ${
                                  router.pathname === item.href && "bg-landing-orange"
                                }`}
                              ></span>
                            </a>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="px-5 md:px-20 flex flex-col gap-3 pb-8 pt-4">
              {!isAuthenticated && (
                <Link href={routes.dashboard.login}>
                  <a className="flex w-full items-center justify-center font-bold px-4 h-11 text-base whitespace-nowrap transition duration-300 ease-in-out transform rounded bg-landing-blue text-white border border-white/20 hover:scale-105 cursor-pointer shadow">
                    Login
                  </a>
                </Link>
              )}
              {isAuthenticated ? (
                <Link href={routes.dashboard.root}>
                  <a className="flex w-full items-center justify-center font-bold px-4 h-11 text-base whitespace-nowrap transition duration-300 ease-in-out transform rounded bg-landing-cherry text-white hover:scale-105 cursor-pointer shadow">
                    Your Account
                  </a>
                </Link>
              ) : (
                <Link href={routes.register}>
                  <a className="flex w-full items-center justify-center font-bold px-4 h-11 text-base whitespace-nowrap transition duration-300 ease-in-out transform rounded bg-landing-cherry text-white hover:scale-105 cursor-pointer shadow">
                    Sign up
                  </a>
                </Link>
              )}
              <Link href={routes.searchHomes}>
                <a className="flex w-full items-center justify-center font-bold px-4 h-11 text-base whitespace-nowrap transition duration-300 ease-in-out transform rounded bg-landing-orange text-white hover:scale-105 cursor-pointer shadow">
                  Search homes
                </a>
              </Link>
            </div>
          </div>
        ) : (
          <LogoBar theme={theme} open={open} toggleMenu={toggleMenu} className={`pt-6`} />
        )}
      </nav>
    </div>
  );
};

export default HeaderMobile;
